import { useEffect, useMemo, useState } from "react";

/**
 * ToolLogo — renders a tool's brand logo with a graceful cascading
 * fallback chain.
 *
 * Source order (first hit wins, automatic fallback on `onError`):
 *
 *   1. Explicit `tool.logoUrl`              (admin override — single try)
 *   2. Logo.dev for resolved domain          (real brand wordmarks/icons,
 *                                             needs REACT_APP_LOGO_DEV_TOKEN)
 *   3. Google S2 favicon for resolved domain (free, no key, broad coverage)
 *   4. Letter monogram                       (final guarantee — never broken)
 *
 * The "resolved domain" is `tool.domain` if the admin set one, otherwise
 * the host extracted from `tool.url` (sans `www.`). So no per-tool
 * configuration is required to get real logos out of the box — the
 * existing 35-tool catalogue gets recognisable brand icons immediately
 * via Google favicons, and "premium" wordmark logos automatically once a
 * Logo.dev token is configured.
 *
 * Background — Why not Clearbit?
 *   The previous implementation used `logo.clearbit.com`. Clearbit's
 *   public Logo API was permanently shut down on 2025-12-01 after
 *   HubSpot's acquisition, so 100% of those requests now fail. Logo.dev
 *   is Clearbit's officially-endorsed successor; Google's S2 favicon
 *   service is a no-key fallback so cards never appear "broken" when
 *   Logo.dev is unset, throttled, or returns 404.
 *
 * Container styling per the brief:
 *   - 48px square, rounded-xl
 *   - light: #F5F5F7 surface
 *   - dark:  #1F1F23 surface
 *   - inner padding so the logo doesn't touch the edges
 *   - object-contain so logos never stretch or get cropped
 *   - lazy loading
 *
 * Accessibility:
 *   - Real `alt` text "{tool.name} logo" when the image renders
 *   - Monogram fallback is `aria-hidden` (the surrounding card already
 *     announces the tool name as its heading).
 */

// Publishable Logo.dev token (browser-safe — Logo.dev calls these
// "publishable" tokens and they are designed to ship in client code).
// Stored in `.env` as REACT_APP_LOGO_DEV_TOKEN. When unset the cascade
// silently skips Logo.dev and goes straight to Google favicons, so the
// component still renders real icons even before a token is plugged in.
//
// Note: CRA's DefinePlugin replaces `process.env.REACT_APP_*` references
// inline at build time. Do NOT wrap this in a `typeof process` guard —
// the guard evaluates to `false` in the browser (process isn't a real
// global) and would leave the token permanently empty in production.
const LOGO_DEV_TOKEN = process.env.REACT_APP_LOGO_DEV_TOKEN || "";

function urlToDomain(url) {
  if (!url || typeof url !== "string") return null;
  try {
    const u = new URL(url);
    return u.hostname.replace(/^www\./, "");
  } catch {
    return null;
  }
}

/**
 * Build the ordered source list for a given tool.
 * Returns an array of absolute URLs to try in order.
 */
function buildLogoSources(tool) {
  if (!tool) return [];
  const list = [];

  // 1) Admin-supplied direct logo URL — highest priority.
  const explicit = (tool.logoUrl || "").trim();
  if (explicit) list.push(explicit);

  // Resolve a domain to drive 2) and 3).
  const adminDomain = (tool.domain || "").trim().toLowerCase();
  const derived = urlToDomain(tool.url);
  const domain = adminDomain || derived;

  if (domain) {
    const enc = encodeURIComponent(domain);

    // 2) Logo.dev — real brand wordmarks/icons, requires token.
    //    `fallback=404` ensures their CDN actually 404s instead of
    //    returning a generic monogram — that lets our `onError`
    //    advance to the next source cleanly.
    if (LOGO_DEV_TOKEN) {
      list.push(
        `https://img.logo.dev/${enc}?token=${encodeURIComponent(
          LOGO_DEV_TOKEN
        )}&size=128&format=png&fallback=404`
      );
    }

    // 3) Google S2 favicons — free, no key, very broad coverage.
    //    Returns a 32–128px PNG. For brands without a real favicon
    //    Google serves a generic globe icon, but every major AI brand
    //    in the catalogue (OpenAI, Google, Microsoft, Anthropic, etc.)
    //    ships proper favicons.
    list.push(
      `https://www.google.com/s2/favicons?sz=128&domain=${enc}`
    );
  }

  return list;
}

export default function ToolLogo({
  tool,
  size = 48,
  className = "",
  testId = "tool-logo",
}) {
  const sources = useMemo(() => buildLogoSources(tool), [tool]);
  const [idx, setIdx] = useState(0);

  // Reset the cascade index whenever the source list changes — e.g.
  // an admin saves a new logoUrl/domain in another tab and the prop
  // updates, or the user navigates between detail views.
  const sourcesKey = sources.join("|");
  useEffect(() => {
    setIdx(0);
  }, [sourcesKey]);

  // Compute monogram letter once. Works for Latin brand names ("ChatGPT"
  // → C) and Arabic names alike (returns the first non-whitespace
  // glyph of the name).
  const initial = ((tool && tool.name) || "?").trim().charAt(0).toUpperCase();

  // Monogram fallback — used when the source list is empty or all
  // network sources have errored. Same visual language as the previous
  // letter-only chip, so cards without a logo still look intentional.
  const renderMonogram = () => (
    <div
      className={`flex items-center justify-center shrink-0 rounded-xl border border-[rgba(111,74,229,0.18)] ${className}`}
      style={{
        width: size,
        height: size,
        background: "var(--sas-accent-soft)",
        color: "var(--sas-accent)",
      }}
      aria-hidden
      data-testid="tool-monogram"
    >
      <span
        className="font-bold leading-none"
        style={{ fontSize: Math.round(size * 0.42) }}
      >
        {initial}
      </span>
    </div>
  );

  // No sources resolvable, or all sources have errored out -> monogram.
  if (sources.length === 0 || idx >= sources.length) {
    return renderMonogram();
  }

  const src = sources[idx];

  return (
    <div
      className={`tool-logo-surface flex items-center justify-center shrink-0 rounded-xl border ${className}`}
      style={{
        width: size,
        height: size,
        padding: Math.round(size * 0.16), // ≈ 7-8px at 48px container
      }}
      data-testid={testId}
    >
      <img
        // Keying by src ensures React unmounts/remounts the <img> when
        // we advance the cascade, otherwise some browsers cache the
        // failed state and `onError` won't fire for the new src.
        key={src}
        src={src}
        alt={`${(tool && tool.name) || ""} logo`}
        loading="lazy"
        decoding="async"
        referrerPolicy="no-referrer"
        onError={() => setIdx((i) => i + 1)}
        className="block w-full h-full object-contain select-none"
        data-testid={`${testId}-img`}
        draggable={false}
      />
    </div>
  );
}
